import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, ChevronLeft, ChevronRight, BookOpen, Filter, X, RefreshCw, Box, ArrowUpDown, ChevronsLeft, ChevronsRight, List, GitBranch, ChevronDown, ChevronRight as ChevronRightIcon, Folder, FolderOpen, Circle, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import api from './axiosConfig';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';
import { translateAccountDescription } from './glDefaultAccounts';

const buildAccountTree = (accounts) => {
  const byCode = {};
  accounts.forEach(a => { byCode[a.glaccount] = { ...a, children: [] }; });
  const roots = [];
  accounts.forEach(a => {
    const node = byCode[a.glaccount];
    if (a.parent_account && byCode[a.parent_account]) {
      byCode[a.parent_account].children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (nodes) => {
    nodes.sort((x, y) => (x.glaccount || '').localeCompare(y.glaccount || ''));
    nodes.forEach(n => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
};

// 🎯 عرض الرصيد كرقم واحد بس (مش عمودين مدين/دائن زي ميزان المراجعة) -
// موجب أخضر (رصيد على الجانب الطبيعي مدين)، سالب أحمر (على جانب دائن).
// balance = closing_debit لو موجب، وإلا -closing_credit - بالظبط نفس
// المنطق اللي compute_trial_balance بيبنيه أصلًا (رقم واحد مصفّى بس)
const BalanceTag = ({ bal }) => {
  if (!bal) return <span className="text-ink-secondary/40 text-[10px] font-normal normal-case">—</span>;
  const value = (bal.closing_debit || 0) - (bal.closing_credit || 0);
  if (Math.abs(value) < 0.005) return <span className="text-ink-secondary/40 text-[10px] font-normal normal-case">0.00</span>;
  const isDebit = value > 0;
  return (
    <span className={`text-[11px] font-black tabular-nums normal-case ${isDebit ? 'text-status-success' : 'text-status-danger'}`}>
      {Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
};

// 🎯 عرض شجري نضيف زي أي File Explorer - مش جدول بأعمدة، صف واحد لكل
// حساب (أيقونة + كود + وصف + رصيد)، مجلد للحسابات المجمّعة ونقطة
// للحسابات التحليلية، من غير خطوط تقسيم رأسية بين المستويات
const AccountTreeNode = ({ node, depth, isRTL, t, onRowClick, expanded, toggleExpanded, balanceMap }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = expanded.has(node.glaccount);
  const bal = balanceMap?.get(node.glaccount);

  return (
    <>
      <div
        className="flex items-center gap-2 py-2 rounded-md hover:bg-primary-soft cursor-pointer transition-colors group"
        style={{ [isRTL ? 'paddingRight' : 'paddingLeft']: `${8 + depth * 26}px`, [isRTL ? 'paddingLeft' : 'paddingRight']: '10px' }}
        onClick={() => onRowClick(node.glaccount)}
      >
        {hasChildren ? (
          <span
            onClick={(e) => { e.stopPropagation(); toggleExpanded(node.glaccount); }}
            className="text-ink-secondary hover:text-primary shrink-0 p-0.5 rounded hover:bg-white"
          >
            {isOpen ? <ChevronDown size={13} /> : (isRTL ? <ChevronLeft size={13} /> : <ChevronRightIcon size={13} />)}
          </span>
        ) : (
          <span className="w-[18px] shrink-0" />
        )}

        {hasChildren ? (
          isOpen
            ? <FolderOpen size={16} className="text-status-warning shrink-0" fill="currentColor" fillOpacity={0.15} />
            : <Folder size={16} className="text-status-warning shrink-0" fill="currentColor" fillOpacity={0.15} />
        ) : (
          <Circle size={7} className="text-primary/50 shrink-0 mx-0.5" fill="currentColor" />
        )}

        <span className={`normal-case italic text-[13px] truncate ${hasChildren ? 'font-black text-ink' : 'font-bold text-primary group-hover:underline'}`}>
          <span className="not-italic font-black tracking-tighter">{node.glaccount}</span>
          <span className="text-ink-secondary"> - {translateAccountDescription(node, t)}</span>
        </span>

        {!node.is_active && (
          <span className="text-[8px] font-black uppercase text-ink-secondary bg-surface-panel px-1.5 py-0.5 rounded shrink-0">
            {t('gl.accounts.fields.inactive', 'Inactive')}
          </span>
        )}

        <span className="flex-1" />

        <span className="text-[9px] font-bold uppercase text-ink-secondary/60 shrink-0 hidden sm:inline">{node.account_type}</span>
        <span className={`w-24 shrink-0 ${isRTL ? 'text-left' : 'text-right'}`}><BalanceTag bal={bal} /></span>
      </div>
      {hasChildren && isOpen && node.children.map(child => (
        <AccountTreeNode
          key={child.glaccount}
          node={child}
          depth={depth + 1}
          isRTL={isRTL}
          t={t}
          onRowClick={onRowClick}
          expanded={expanded}
          toggleExpanded={toggleExpanded}
          balanceMap={balanceMap}
        />
      ))}
    </>
  );
};

const GLAccountList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const { fontSize } = useFontSize();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissions('gl-accounts');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState('list');
  const [expanded, setExpanded] = useState(new Set());
  const [balanceMap, setBalanceMap] = useState(new Map());

  const q = searchParams.get('q') || "";

  const tree = useMemo(() => buildAccountTree(data), [data]);
  const allGroupCodes = useMemo(() => data.filter(a => data.some(x => x.parent_account === a.glaccount)).map(a => a.glaccount), [data]);

  const toggleExpanded = (code) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(allGroupCodes));
  const collapseAll = () => setExpanded(new Set());

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionOrg = session.org_id || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/gl/accounts', { params: { org_id: sessionOrg, search: q } });
      setData(res.data.data);
      setTotalCount(res.data.total);
    } catch (err) {
      toast.error(t('common.load_error', "Failed to load accounts"));
    } finally {
      setLoading(false);
    }
  }, [q, sessionOrg, t]);

  // 🎯 الأرصدة (ورصيد الآباء المجمّع من تحت لفوق) بنجيبها من نفس منطق
  // ميزان المراجعة (compute_trial_balance) بالظبط - من غير from_date
  // يعني "من أول ما اتفتح الحساب لحد النهاردة"، وshow_zero عشان يبان كل
  // حساب حتى لو لسه مفيش حركة عليه (شجرة الحسابات لازم تفضل كاملة)
  const fetchBalances = useCallback(async () => {
    try {
      const res = await api.get('/gl/trial-balance', {
        params: { org_id: sessionOrg, to_date: new Date().toISOString().substring(0, 10), show_zero: true, show_groups: true }
      });
      const map = new Map((res.data.data || []).map(r => [r.glaccount, r]));
      setBalanceMap(map);
    } catch (err) {
      // صامت - الأرصدة تحسين إضافي، مش المفروض توقف عرض شجرة الحسابات
    }
  }, [sessionOrg]);

  useEffect(() => {
    const timer = setTimeout(() => { fetchData(); fetchBalances(); }, 100);
    return () => clearTimeout(timer);
  }, [fetchData, fetchBalances]);

  useEffect(() => {
    const withChildren = new Set(data.filter(a => a.parent_account && data.some(x => x.glaccount === a.parent_account)).map(a => a.parent_account));
    setExpanded(withChildren);
  }, [data]);

  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleRowClick = (glaccount) => {
    navigate(`/gl-accounts/${glaccount}`);
  };

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>

      <div className="bg-surface-card p-3 border-b shadow-sm z-20">
        <div className="flex justify-between items-center px-4">
          <h2 className="text-primary-dark font-black italic text-lg flex items-center gap-2">
            <BookOpen size={22} className="text-status-warning" />
            {t('gl.accounts.title', 'Chart of Accounts')}
            <RefreshCw
              size={18}
              className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`}
              onClick={() => fetchData()}
            />
          </h2>

          <div className="flex gap-3 items-center">
            <div className={`relative flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Search className={`absolute text-ink-secondary ${isRTL ? 'right-2' : 'left-2'}`} size={14} />
              <input
                className={`w-52 p-1.5 text-primary font-bold outline-none border border-line-subtle rounded-sm focus:border-primary bg-surface-card ${isRTL ? 'pr-8' : 'pl-8'}`}
                placeholder={t('gl.accounts.search', 'Search accounts...')}
                value={q}
                onChange={e => updateFilters({ q: e.target.value })}
              />
            </div>
            <div className="flex border border-line-subtle rounded-sm overflow-hidden">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-2 font-black transition-colors ${viewMode === 'list' ? 'bg-primary-dark text-white' : 'bg-surface-card text-ink-secondary hover:text-primary'}`}
              >
                <List size={14} /> {t('common.list_view', 'List View')}
              </button>
              <button
                onClick={() => setViewMode('tree')}
                className={`flex items-center gap-1.5 px-3 py-2 font-black transition-colors ${viewMode === 'tree' ? 'bg-primary-dark text-white' : 'bg-surface-card text-ink-secondary hover:text-primary'}`}
              >
                <GitBranch size={14} /> {t('common.tree_view', 'Tree View')}
              </button>
            </div>
            {viewMode === 'tree' && (
              <div className="flex border border-line-subtle rounded-sm overflow-hidden">
                <button onClick={expandAll} title={t('gl.accounts.expand_all', 'Expand All')} className="flex items-center gap-1.5 px-3 py-2 font-black bg-surface-card text-ink-secondary hover:text-primary transition-colors">
                  <ChevronsUpDown size={14} />
                </button>
                <button onClick={collapseAll} title={t('gl.accounts.collapse_all', 'Collapse All')} className={`flex items-center gap-1.5 px-3 py-2 font-black bg-surface-card text-ink-secondary hover:text-primary transition-colors border-line-subtle ${isRTL ? 'border-r' : 'border-l'}`}>
                  <ChevronsDownUp size={14} />
                </button>
              </div>
            )}
            <button onClick={() => setSearchParams({})} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
              <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
            </button>
            {canCreate && (
              <button onClick={() => navigate('/gl-accounts/new')} className="bg-primary-dark text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-primary shadow-lg transition-all">
                <Plus size={16} /> {t('gl.accounts.new', 'NEW ACCOUNT')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-20 text-center font-black text-ink-secondary animate-pulse tracking-[0.2em]">{t('common.syncing', 'Syncing Data...')}</div>
          ) : data.length === 0 ? (
            <div className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('gl.accounts.no_records', 'No Accounts Found')}</div>
          ) : viewMode === 'tree' ? (
            <div className="p-2">
              {tree.map(node => (
                <AccountTreeNode
                  key={node.glaccount}
                  node={node}
                  depth={0}
                  isRTL={isRTL}
                  t={t}
                  onRowClick={handleRowClick}
                  expanded={expanded}
                  toggleExpanded={toggleExpanded}
                  balanceMap={balanceMap}
                />
              ))}
            </div>
          ) : (
            <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
              <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
                <tr>
                  <th className="p-3 border-x border-white/10">{t('gl.accounts.fields.code', 'Account Code')}</th>
                  <th className="p-3 border-x border-white/10">{t('gl.accounts.fields.description', 'Description')}</th>
                  <th className="p-3 border-x border-white/10 text-center">{t('gl.accounts.fields.type', 'Type')}</th>
                  <th className="p-3 border-x border-white/10 text-center">{t('gl.trial_balance.fields.balance', 'Balance')}</th>
                  <th className="p-3 border-x border-white/10 text-center">{t('gl.accounts.fields.active', 'Active')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-subtle italic font-bold">
                {data.map((item) => (
                  <tr key={item.glaccount} className="hover:bg-primary-soft cursor-pointer transition-colors group" onClick={() => handleRowClick(item.glaccount)}>
                    <td className="p-3 border-x border-line-subtle font-black text-primary group-hover:underline">{item.glaccount}</td>
                    <td className="p-3 border-x border-line-subtle text-ink">{translateAccountDescription(item, t)}</td>
                    <td className="p-3 border-x border-line-subtle text-center">
                      <span className="bg-status-warning-bg text-status-warning px-2 py-0.5 rounded-full text-[9px] shadow-sm border border-status-warning-bg">
                        {item.account_type}
                      </span>
                    </td>
                    <td className="p-3 border-x border-line-subtle text-center"><BalanceTag bal={balanceMap.get(item.glaccount)} /></td>
                    <td className="p-3 border-x border-line-subtle text-center">
                      {item.is_active ? (
                        <span className="text-status-success">●</span>
                      ) : (
                        <span className="text-ink-secondary">●</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className={`bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black text-[10px] ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Filter size={14} className="text-status-warning"/>
          <span>{t('common.total', 'Total')}: <span className="text-status-warning">{totalCount}</span></span>
        </div>
      </div>
    </div>
  );
};

export default GLAccountList;
